import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as THREE from 'three';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/* ── Smooth Split Text ── */
function SplitText({ text, delay = 0, className = "" }) {
  return (
    <span className={className}>
      {text.split('').map((char, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: '80%', filter: 'blur(8px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{
            duration: 1.2,
            delay: delay + i * 0.04,
            ease: [0.16, 1, 0.3, 1],
          }}
          style={{ display: 'inline-block', transformOrigin: 'bottom' }}
        >
          <span style={{ display: 'inline-block' }}>{char === ' ' ? '\u00A0' : char}</span>
        </motion.span>
      ))}
    </span>
  );
}

/* ── Procedural Mic Scene with GSAP ScrollTrigger ── */
function MicScene() {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(canvas.clientWidth, canvas.clientHeight);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;

    // Mobile: portrait viewport is narrow — mic at x=1.5 is completely off-screen.
    // Bring camera closer and center the mic so it fills the screen properly.
    const isMobile = canvas.clientWidth < 768;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, canvas.clientWidth / canvas.clientHeight, 0.1, 100);
    camera.position.set(0, 0, isMobile ? 4.5 : 8);

    // Studio Lighting
    scene.add(new THREE.AmbientLight(0xFFFFFF, 0.4));

    const key = new THREE.PointLight(0xFFFFFF, 3, 15);
    key.position.set(3, 4, 3);
    scene.add(key);

    const rim = new THREE.DirectionalLight(0xC8922A, 2);
    rim.position.set(-3, 0, -3);
    scene.add(rim);

    const fill = new THREE.PointLight(0xFFE8C0, 1.5, 10);
    fill.position.set(-4, -1, 3);
    scene.add(fill);

    const micGroup = new THREE.Group();
    micGroup.position.set(isMobile ? 0 : 1.5, isMobile ? -0.6 : -0.2, 0);
    if (isMobile) micGroup.scale.setScalar(0.8);

    const darkMat = new THREE.MeshStandardMaterial({ color: 0x111111, metalness: 0.8, roughness: 0.2 });
    const goldMat = new THREE.MeshStandardMaterial({ color: 0xC8922A, metalness: 0.9, roughness: 0.15 });
    
    const body = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 1.5, 32), darkMat);
    micGroup.add(body);

    const dome = new THREE.Mesh(new THREE.SphereGeometry(0.32, 32, 32, 0, Math.PI * 2, 0, Math.PI * 0.55), darkMat);
    dome.position.y = 0.8;
    micGroup.add(dome);

    const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.4, 32), goldMat);
    neck.position.y = -0.95;
    micGroup.add(neck);

    for (let i = 0; i < 5; i++) {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.31, 0.015, 16, 64), goldMat);
      ring.position.y = 0.3 + i * 0.3;
      ring.rotation.x = Math.PI / 2;
      micGroup.add(ring);
    }
    scene.add(micGroup);

    // Particles
    const particlesGeo = new THREE.BufferGeometry();
    const pCount = 120;
    const pPos = new Float32Array(pCount * 3);
    for (let i = 0; i < pCount * 3; i++) {
      pPos[i] = (Math.random() - 0.5) * 12;
    }
    particlesGeo.setAttribute('position', new THREE.BufferAttribute(pPos, 3));
    const pMat = new THREE.PointsMaterial({ color: 0xC8922A, size: 0.03, transparent: true, opacity: 0.6 });
    const particles = new THREE.Points(particlesGeo, pMat);
    scene.add(particles);

    // --- GSAP Integration ---
    const micData = {
      x: isMobile ? 0 : 1.5,
      y: isMobile ? -0.6 : -0.2,
      z: 0,
      rotX: 0, rotY: 0, rotZ: 0,
      scale: isMobile ? 0.8 : 1,
    };

    let ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: "#stats-section",
          start: "top bottom",
          end: "center center",
          scrub: 1,
        }
      });

      tl.to(micData, {
        // Mobile: stay centered, just rotate and come forward slightly.
        // Desktop: swing left as before.
        x: isMobile ? 0 : -2.0,
        y: isMobile ? -0.4 : 0.2,
        z: isMobile ? 0.8 : 1.5,
        rotX: 0.2,
        rotY: -Math.PI / 3,
        scale: isMobile ? 0.9 : 1.1,
        ease: "none"
      });
    });

    // Mouse Tracking
    let mx = 0, my = 0;
    const onMove = (e) => {
      mx = (e.clientX / window.innerWidth - 0.5) * 2;
      my = -(e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);

    let scrollY = window.scrollY;
    const onScroll = () => { scrollY = window.scrollY; };
    window.addEventListener('scroll', onScroll, { passive: true });

    const clock = new THREE.Clock();
    let curRotX = 0, curRotY = 0;

    const animate = () => {
      rafRef.current = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();

      // Smooth tracking setup
      curRotX += (my * 0.15 - curRotX) * 0.05;
      curRotY += (mx * 0.25 - curRotY) * 0.05;

      micGroup.position.set(
        micData.x,
        micData.y + Math.sin(t * 0.8) * 0.08,
        micData.z
      );

      micGroup.rotation.set(
        micData.rotX + curRotX + Math.sin(t * 0.5) * 0.05,
        micData.rotY + curRotY + Math.sin(t * 0.3) * 0.1,
        micData.rotZ
      );

      micGroup.scale.setScalar(micData.scale);

      particles.rotation.y = t * 0.02 + mx * 0.1;
      particles.position.y = scrollY * 0.002;

      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      renderer.setSize(canvas.clientWidth, canvas.clientHeight);
      camera.aspect = canvas.clientWidth / canvas.clientHeight;
      // Recalculate camera depth if device orientation changed
      const nowMobile = canvas.clientWidth < 768;
      camera.position.z = nowMobile ? 4.5 : 8;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      ctx.revert();
      renderer.dispose();
      darkMat.dispose();
      goldMat.dispose();
      particlesGeo.dispose();
      pMat.dispose();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute', inset: 0,
        width: '100%', height: '100%',
        pointerEvents: 'none', zIndex: 0
      }}
    />
  );
}



export default function Hero() {
  return (
    <section id="hero-wrapper" style={{ position: 'relative', background: '#100C06' }}>
      
      {/* Sticky 3D Canvas Layer */}
      <div style={{ position: 'sticky', top: 0, height: '100vh', width: '100%', zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <span className="ghost-text" style={{ position: 'absolute', top: '15%', left: '-5%', fontSize: 'clamp(140px,25vw,400px)', opacity: 0.03, color: '#C8922A', zIndex: 0 }}>आरोहण</span>
        <MicScene />
      </div>

      {/* Scrollable Foreground Layers */}
      <div style={{ position: 'relative', zIndex: 10, marginTop: '-100vh' }}>
        
        {/* === ORIGINAL HERO CONTENT (100vh) === */}
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', position: 'relative', pointerEvents: 'none' }}>
          


          <div style={{ padding: '0 6%', maxWidth: 740, width: '100%', pointerEvents: 'auto' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(32px, 4vw, 56px)', fontWeight: 300, color: 'rgba(250,247,240,0.9)', marginBottom: 24, letterSpacing: '-0.5px' }}>
                <div style={{ overflow: 'hidden', paddingBottom: 8 }}>
                  <SplitText text="Where Sound " delay={0.4} />
                  <em style={{ color: '#C8922A', fontStyle: 'italic' }}>
                    <SplitText text="Becomes Art." delay={0.6} />
                  </em>
                </div>
              </h2>
            </div>
            
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              style={{
                fontSize: 16, lineHeight: 1.9,
                color: 'rgba(250,247,240,0.5)',
                maxWidth: 480, marginBottom: 56, fontWeight: 300,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              A sanctuary for sound. State-of-the-art Dolby Atmos mixing, mastering, and the city's premier institute for musical arts equipped with AI intelligence.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 1.2, ease: [0.16, 1, 0.3, 1] }} style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              <Link to="/book" className="btn-liquid" data-cursor="BOOK" style={{ borderRadius: 2, textDecoration: 'none' }}>Book a Session</Link>
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.2, duration: 1.5 }}
            style={{
              position: 'absolute', bottom: 40, left: '6%',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
            }}
          >
            <motion.div
              animate={{ height: [0, 48, 0], opacity: [0, 1, 0], y: [0, 24, 48] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
              style={{ width: 1, background: '#C8922A' }}
            />
          </motion.div>
        </div>

        {/* === NEW CONTINUOUS STATS SCROLL SECTION (100vh) === */}
        {/* === NEW CONTINUOUS STATS SCROLL SECTION (100vh) === */}
        <div id="stats-section" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', padding: '0 6%', pointerEvents: 'none' }}>
          
          <div className="hide-mobile" style={{ flex: 1 }}></div>
          
          <div style={{ flex: 1, pointerEvents: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 24, paddingBottom: '10vh', width: '100%', height: '100%' }}>
            
            <span style={{ fontSize: 13, letterSpacing: '4px', color: '#C8922A', textTransform: 'uppercase', fontFamily: "'DM Sans', sans-serif", fontWeight: 600 }}>Keep Scrolling</span>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(40px, 5vw, 64px)', fontWeight: 300, color: '#FAF7F0', lineHeight: 1.2, margin: 0, textAlign: 'center' }}>
              Explore our world of <br/> sound and music.
            </h2>
            
          </div>
        </div>

      </div>
    </section>
  );
}
